import { makePage } from '@/components/page'
import { useVenueContext } from '@/services/venue'
import Link from 'next/link'
import { LegacyInput, InputRef, touchInputs, SelectRef, Select } from '@/modern/components/input'
import { sessionStorage, stubStorage } from '@/utils/env'
import React, { useRef, useState, useEffect } from 'react'
import { VasActions } from '@/components/modern/modern-vas-sheet'
import { useAppContext } from '@/pages/_app'
import { VenueBookTableBody } from 'shared/types/api'
import { range } from 'shared/utils/array'
import { resolveI18n } from 'shared/i18n'
import addDays from 'date-fns/addDays'
import format from 'date-fns/format'
import setMinutes from 'date-fns/setMinutes'
import setHours from 'date-fns/setHours'
import { CalendarIcon } from '@/components/calendar-icon'
import { ClockIcon } from '@/components/clock-icon'
import { PersonIcon } from '@/components/person-icon'
import { Spinner } from '@/components/spinner'
import { InputPhone } from '@/modern/components/input-phone'
import { capitalize } from 'shared/utils/string'
import { generateTimeOptions, localizeTimeString, parseTime } from '@/utils/date'
import { returnOrThrow } from 'shared/utils/types'
import { getZonedParts, isValidTimeZone, zonedLocalToIso } from 'shared/availability'
import { PageMeta } from '@/components/page-meta'
import { HeadPanel } from '@/modern/components/head-panel'
import { TrixContent } from '@/modern/components/trix-content'
import {
  checkPersonalDataConsent,
  PersonalDataConsent,
} from '@/modern/components/personal-data-consent'
import { getTableFromQr, getTableQrPickerGroups } from '@/services/table-qr'
import { TableQrPicker, TableQrPickerValue } from '@/components/table-qr-picker'
import { formatTableQrWish } from 'shared/table-qr'

/** Тело экрана брони: страница по прямой ссылке и шторка внутри меню. */
export function BookTableContent(props: { asSheet?: boolean }) {
  const { apiClient, tracker, t, i18n } = useAppContext()
  const venueContext = useVenueContext()!
  const { venueName, venue, menuRoute, locale } = venueContext

  const refs = {
    numberOfGuests: useRef<SelectRef>(null),
    date: useRef<SelectRef>(null),
    time: useRef<SelectRef>(null),
    phone: useRef<InputRef>(null),
    comment: useRef<InputRef>(null),
    name: useRef<InputRef>(null),
    email: useRef<InputRef>(null),
  }

  type Status =
    | {
        type: 'initial'
      }
    | {
        type: 'loading'
        sentBookingDate: Date
      }
    | {
        type: 'reservationMade'
        formResult: VenueBookTableBody
      }
    | {
        type: 'error'
      }

  const [status, setStatus] = useState<Status>({
    type: 'initial',
  })
  const [todayIsChosen, setTodayIsChosen] = useState(true)
  // «Обязательно» у телефона — после первой попытки отправить, а не до ввода
  // (M-52). Общее поле телефона подписывает пустое обязательное поле сразу,
  // поэтому обязательным оно становится с этой попытки; пустой телефон до неё
  // всё равно не пропускает проверка в onSubmit
  const [phoneRequired, setPhoneRequired] = useState(false)

  const bookTableConfig = venue.features.vas?.bookTable

  // Шторка брони живёт в меню с его открытия: без пересчёта первым так и
  // оставался уже прошедший слот («12:45» в 12:46). Пересчёт раз в 30 с
  const [, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => {
      setTick((tick) => {
        return tick + 1
      })
    }, 30000)
    return () => {
      clearInterval(timer)
    }
  }, [])

  // «Сейчас» и «сегодня» — по часам заведения, если у него задан пояс
  // (features.timezone, master #139): слоты — его часы, и в заведение время
  // уходит с его смещением (12:45 по Алматы — «…T12:45:00+05:00», а не со
  // смещением браузера гостя). Без пояса — по часам браузера, как в прежнем
  // оформлении. Даты ниже — дни заведения
  const venueTimeZone =
    venue.features.timezone && isValidTimeZone(venue.features.timezone)
      ? venue.features.timezone
      : null
  const venueNow = venueTimeZone ? getZonedParts(new Date(), venueTimeZone) : localParts(new Date())
  const venueToday = new Date(venueNow.year, venueNow.month - 1, venueNow.day)
  const now = setMinutes(setHours(new Date(), venueNow.hour), venueNow.minute)

  const opensAt = returnOrThrow(parseTime(bookTableConfig?.opensAt || '08:00'))
  const closesAt = returnOrThrow(parseTime(bookTableConfig?.closesAt || '23:59'))
  const stepMinutes = 15
  const timeOptions = generateTimeOptions(opensAt, closesAt, stepMinutes)

  const timeOptionsToday = timeOptions.filter((v) => {
    return v > now
  })

  useEffect(() => {
    if (timeOptionsToday.length === 0) {
      setTodayIsChosen(false)
    }
  }, [])

  // «QR-коды столов»: пожелание по залу и столу — как в прежнем оформлении
  // (необязательно, «Любой зал / Любой стол»); пришедшему по QR стола его
  // стол подставлен сразу. Без QR столов у заведения выбора нет, как было
  const tablePickerGroups = getTableQrPickerGroups(venue)
  const [wishPick, setWishPick] = useState<TableQrPickerValue>({ sectionKey: '', number: '' })
  useEffect(() => {
    const tableFromQr = getTableFromQr(venue)
    if (tableFromQr) {
      setWishPick({ sectionKey: tableFromQr.sectionId ?? '', number: tableFromQr.number })
    }
  }, [venue.name])

  if (!bookTableConfig) {
    return null
  }

  const formId = 'bookTable'

  return (
    <div>
      {!props.asSheet && (
        <>
          <PageMeta
            subTitle={t('bookTable:bookTable')}
            description={t('bookTable:description', { venueName: venue.title })}
          />
          <HeadPanel url={menuRoute.path} text={t('bookTable:bookTable')} />
        </>
      )}
      {(() => {
        if (status.type === 'reservationMade') {
          return (
            <>
              <div className="mt-4 mb-3 bg-backAccent dark:bg-backAccentDark px-5 py-5 text-lg leading-tight">
                {t('bookTable:thankYou')}
              </div>
              {bookTableConfig.noticeHtml && (
                <TrixContent
                  className="px-5 mt-2 text-lg"
                  unescapedHtml={resolveI18n(
                    bookTableConfig.noticeHtmlI18n,
                    bookTableConfig.noticeHtml,
                    i18n.language,
                  )}
                />
              )}
              <div className="flex justify-between px-5 mt-6 text-lg">
                <div className="flex items-center">
                  <CalendarIcon />
                  <span className="ml-1">
                    {format(wallClock(status.formResult.date), 'eee, d MMM', { locale })}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="w-5 h-5">
                    <ClockIcon />
                  </span>
                  <span className="ml-1">
                    {format(wallClock(status.formResult.date), 'HH:mm', { locale })}
                  </span>
                </div>
                <div className="flex items-center">
                  <PersonIcon />
                  <span className="ml-1">
                    {(() => {
                      const { numberOfGuests } = status.formResult
                      return numberOfGuests === 999 ? '10+' : numberOfGuests
                    })()}{' '}
                    {t('bookTable:people')}
                  </span>
                </div>
              </div>
              <div className="px-5 mt-6 text-lg">
                {status.formResult.name && (
                  <div className="mt-1">
                    {t('common:name')}: {status.formResult.name}
                  </div>
                )}
              </div>
              <div className="px-5 mt-2 text-lg">
                {status.formResult.phone && (
                  <div className="mt-1">
                    {t('common:phoneNumber')}: {status.formResult.phone}
                  </div>
                )}
              </div>
              <div className="px-5 mt-2 text-lg">
                {status.formResult.email && (
                  <div className="mt-1">
                    {t('common:email')}: {status.formResult.email}
                  </div>
                )}
              </div>
              <div className="px-5 mt-2 text-lg">
                {(() => {
                  const wish = formatTableQrWish(
                    venue.features.tableQr,
                    status.formResult.wishSectionId,
                    status.formResult.wishTableNumber,
                    i18n.language,
                  )
                  return wish ? (
                    <div className="mt-1">
                      {t('tableQr:bookingWish')}: {wish}
                    </div>
                  ) : null
                })()}
              </div>
              <div className="px-5 mt-2 text-lg">
                {status.formResult.comment && (
                  <div className="mt-1 whitespace-pre-line">
                    {t('common:note')}: {status.formResult.comment}
                  </div>
                )}
              </div>
            </>
          )
        }

        return (
          <>
            {bookTableConfig.addressHtml && (
              <TrixContent
                className="mt-4 mb-3 bg-backAccent dark:bg-backAccentDark px-5 py-5 text-lg leading-tight"
                unescapedHtml={resolveI18n(
                  bookTableConfig.addressHtmlI18n,
                  bookTableConfig.addressHtml,
                  i18n.language,
                )}
              />
            )}
            <form
              id={formId}
              className="book-form"
              onSubmit={async (event) => {
                event.preventDefault()
                const isValid = touchInputs(Object.values(refs)).isValid
                // телефон обязателен всегда; помечаем его так с первой попытки
                const phoneIsEmpty = !refs.phone.current?.value.trim()
                if (phoneIsEmpty) {
                  setPhoneRequired(true)
                }
                if (!isValid || phoneIsEmpty || status.type !== 'initial') {
                  return
                }

                if (!checkPersonalDataConsent()) {
                  return
                }

                // день и время по часам заведения: бэк показывает заведению
                // именно выбранные «дату и время на часах»
                const bookingDate = addDays(
                  venueToday,
                  Number.parseInt(refs.date.current!.value, 10),
                )
                // we are sure hours and minutes are defined because we generated the time string ourselves
                const [hours, minutes] = refs.time.current!.value.split(':').map(Number) as [
                  number,
                  number,
                ]
                const bookingDateAndTime = setMinutes(setHours(bookingDate, hours), minutes)
                const localDateTime = format(bookingDateAndTime, "yyyy-MM-dd'T'HH:mm")
                setStatus({
                  type: 'loading',
                  sentBookingDate: bookingDateAndTime,
                })

                const body: VenueBookTableBody = {
                  date:
                    (venueTimeZone && zonedLocalToIso(localDateTime, venueTimeZone)) ||
                    format(bookingDateAndTime, `yyyy-MM-dd'T'HH:mm:ssxxx`),
                  numberOfGuests: Number.parseInt(refs.numberOfGuests.current!.value, 10),
                  name: refs.name.current!.value,
                  phone: refs.phone.current!.implicitValue,
                  email: refs.email.current!.value,
                  comment: refs.comment.current!.value,
                  ...(tablePickerGroups.length > 1 && wishPick.sectionKey
                    ? { wishSectionId: wishPick.sectionKey }
                    : {}),
                  ...(wishPick.number
                    ? {
                        wishTableNumber: wishPick.number,
                        ...(tablePickerGroups.length === 1 && tablePickerGroups[0]?.section
                          ? { wishSectionId: tablePickerGroups[0].section.id }
                          : {}),
                      }
                    : {}),
                }

                tracker.track('user/bookTable', { bookTable: body })
                const apiResponse = await apiClient.fetch(`/venue/${venueName}/book-table`, {
                  method: 'POST',
                  body: JSON.stringify(body),
                })
                window.scroll(0, 0)

                if (apiResponse instanceof Error) {
                  setStatus({
                    type: 'error',
                  })
                  tracker.trackError(apiResponse, {
                    bookTableError: true,
                  })
                  return
                }

                if (apiResponse.status !== 200) {
                  setStatus({
                    type: 'error',
                  })
                  tracker.trackError(new Error('bookTable: error response'), {
                    bookTableError: true,
                    apiResponse,
                  })
                  return
                }

                setStatus({
                  type: 'reservationMade',
                  // pass value, not implicit value to show the user the exact text
                  // they have seen in the phone input
                  formResult: { ...body, phone: refs.phone.current!.value },
                })
              }}
            >
              <div className="px-5 mt-2">
                <div className="mt-6">
                  <Select
                    storage={sessionStorage}
                    ref={refs.numberOfGuests}
                    id="bookTable/numberOfGuests"
                    label={t('bookTable:partySize')}
                    options={[
                      ...range(1, 10)
                        .map(String)
                        .map((v) => {
                          return { id: v, text: v }
                        }),
                      { id: '999', text: capitalize(t('bookTable:moreThanTen')) },
                    ]}
                    initialOptionId="2"
                  />
                </div>
                <div className="mt-6 flex">
                  <div className="flex-grow" style={{ flexBasis: '50%' }}>
                    <Select
                      storage={stubStorage}
                      ref={refs.date}
                      id="bookTable/date"
                      label={t('common:date')}
                      options={[
                        ...(timeOptionsToday.length > 0
                          ? [{ id: '0', text: t('common:today') }]
                          : []),
                        { id: '1', text: t('common:tomorrow') },
                        ...range(2, 7).map((daysFromNow) => {
                          const date = addDays(venueToday, daysFromNow)
                          return {
                            id: daysFromNow.toString(),
                            text: capitalize(
                              format(date, 'eeee, d MMMM', {
                                locale,
                              }),
                            ),
                          }
                        }),
                      ]}
                      onChange={(value) => {
                        setTodayIsChosen(Number.parseInt(value, 10) === 0)
                      }}
                    />
                  </div>
                  <div className="w-4 flex-shrink-0"></div>
                  <div className="flex-grow" style={{ flexBasis: '50%' }}>
                    <Select
                      storage={stubStorage}
                      ref={refs.time}
                      id="bookTable/time"
                      label={t('common:time')}
                      options={(todayIsChosen ? timeOptionsToday : timeOptions)
                        .map((v) => {
                          return format(v, 'HH:mm', { locale })
                        })
                        .map((v) => {
                          return {
                            id: v,
                            text: localizeTimeString(v),
                          }
                        })}
                    />
                  </div>
                </div>
                {tablePickerGroups.length > 0 && (
                  <div className="mt-6 co-table">
                    <TableQrPicker
                      groups={tablePickerGroups}
                      hallLabel={t('tableQr:wishHall')}
                      tableLabel={t('tableQr:wishTable')}
                      value={wishPick}
                      invalid={false}
                      optional
                      onChange={setWishPick}
                    />
                  </div>
                )}
                <div className="mt-6">
                  <LegacyInput
                    storage={sessionStorage}
                    ref={refs.name}
                    id="bookTable/name"
                    label={t('common:name')}
                    type="text"
                    value=""
                  />
                </div>
                <div className="mt-6">
                  <InputPhone label={t('common:phone')} ref={refs.phone} required={phoneRequired} />
                </div>
                <div className="mt-6 custom-book-table-email-field">
                  <LegacyInput
                    storage={sessionStorage}
                    ref={refs.email}
                    id="bookTable/email"
                    label={t('common:email')}
                    type="email"
                    value=""
                  />
                </div>
                <div className="mt-6 mb-6">
                  <LegacyInput
                    storage={sessionStorage}
                    height={2}
                    ref={refs.comment}
                    id="bookTable/comment"
                    label={t('bookTable:note')}
                    type="text"
                    value=""
                  />
                </div>

                <div className="mt-2">
                  <PersonalDataConsent {...{ venue }} />
                </div>
              </div>
            </form>
          </>
        )
      })()}
      <VasActions asSheet={props.asSheet}>
        {status.type === 'reservationMade' ? (
          <Link
            href={menuRoute.path}
            className="flex justify-center items-center h-12 bg-back dark:bg-backAccentDark dark:text-primaryAccentDark font-medium rounded text-center"
          >
            <span className="text-lg">{t('back')}</span>
          </Link>
        ) : (
          <button
            className="flex items-center w-full h-12 text-lg justify-center rounded-lg bg-back dark:bg-backAccentDark dark:text-primaryAccentDark font-medium"
            type="submit"
            form={formId}
          >
            {(() => {
              if (status.type === 'error') {
                return (
                  <span className="font-medium text-negative dark:text-negativeDark cursor-not-allowed">
                    {t('common:error')}
                  </span>
                )
              }
              if (status.type === 'loading') {
                return (
                  <div className="w-6 inline-block mx-auto">
                    <Spinner />
                  </div>
                )
              }

              return <span>{t('bookTable:book')}</span>
            })()}
          </button>
        )}
      </VasActions>
    </div>
  )
}

export default makePage(
  {
    clientRenderOnly: {},
  },
  function BookTablePage() {
    return <BookTableContent />
  },
)

export {
  getVenueStaticPaths as getStaticPaths,
  getVenueStaticProps as getStaticProps,
} from '@/services/venue'

function localParts(date: Date) {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
  }
}

/** время брони на часах, как его выбрал гость: часть строки до смещения пояса */
function wallClock(isoWithOffset: string) {
  return new Date(isoWithOffset.slice(0, 19))
}
